// Form Component to Add or Edit project task

import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import autoTable from "jspdf-autotable";
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Select,
  Space,
  DatePicker,
  Upload,
  Checkbox,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import {
  TDropdownOptions,
  TResponseModel,
  TProjectWork,
} from "../../../../types/config";
import { ePriority, eResultCode, eRoleUniqueId } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  AddEditTask,
  AddEditWork,
  AddEditWorkCategory,
  GetProjectList,
  GetProjectReportById,
  GetEmployeeReport,
  GetSpecificProject,
  GetSpecificWork,
} from "../../../../utils/api.constant";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import {
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import CustomDropdown from "../../../../components/custom-dropdown/custom-dropdown";
import { PageSize } from "../../../../utils/constants";
import dayjs from "dayjs";
import { set } from "lodash";
import Loader from "../../../../components/loader/loader";
import DateUtils from "../../../../utils/date";

type TTDrawerProps = {
  id: number;
  onCloseDrawer: () => void;
};

// generate enum for report type
enum eGENERATE_REPORT_TYPE {
  PROJECT_REPORT = 1,
  EMPLOYEE_REPORT = 2,
}

enum eREPORT_FORMAT {
  GENERATE_EXCEL = 1,
  GENERATE_PDF = 2,
}

enum eREPORT_TYPE {
  ORGANIZATION_REPORT = 1,
  CLIENT_REPORT = 2,
}

interface WorkData {
  id: number;
  projectId: number;
  projectName: string;
  projectCategoryId: number;
  workCategoryId: number;
  managerId: number;
  workTitle: string;
  workDescription: string;
  startDate: string;
  endDate: string;
  taskEstimatedTime: number;
  taskActualTime: number;
  priority: number;
  projectCategoryName: string;
  workCategoryName: string;
  managerName: string;
  employeeName: string;
  totalTask: number;
  taskTitle: string;
  taskDescription: string;
  taskStatus: number;
}

const GenerateReports = (props: TTDrawerProps) => {
  const [form] = Form.useForm(); // Form instance
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast
  const [formValues, setFormValues] = useState<any>({});

  const onFinish = async (values: TProjectWork) => {
    try {
      const payload = {
        data: {
          ...values,
        },
      };
      setIsLoading(true);

      const response = await post(
        formValues.generateReport === eGENERATE_REPORT_TYPE.PROJECT_REPORT
          ? GetProjectReportById
          : GetEmployeeReport,
        payload
      );
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        // form.resetFields();
        // props.onCloseDrawer();

        if (formValues.reportFormat === eREPORT_FORMAT.GENERATE_PDF) {
          exportPdf(response?.data);
        } else {
          exportExcel(response?.data);
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Something went wrong!",
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateReportOptions = [
    { value: eGENERATE_REPORT_TYPE.PROJECT_REPORT, label: "Project Report" },
    { value: eGENERATE_REPORT_TYPE.EMPLOYEE_REPORT, label: "Employee Report" },
  ];

  const reportFormatOptions = [
    { value: eREPORT_FORMAT.GENERATE_EXCEL, label: "Generate Excel" },
    { value: eREPORT_FORMAT.GENERATE_PDF, label: "Generate PDF" },
  ];

  const reportTypeOptions = [
    { value: eREPORT_TYPE.ORGANIZATION_REPORT, label: "Organization Report" },
    { value: eREPORT_TYPE.CLIENT_REPORT, label: "Client Report" },
  ];

  const exportPdf = (data: WorkData[]): void => {
    if (!data || data.length === 0) return;

    const doc = new jsPDF();

    // Function to generate the header on each page
    const generateHeader = (
      currentPage: number,
      pageSize: { width: number; height: number }
    ) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(40);

      // Title centered at the top
      doc.text("Tasks Report", pageSize.width / 2, 15, {
        align: "center",
      });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      // Display total work items on the left
      doc.text(`Total: ${data.length}`, 10, 27);

      // Display current date on the right
      const currentDate = new Date().toLocaleDateString("en-GB");
      doc.text(`Date: ${currentDate}`, pageSize.width - 10, 27, {
        align: "right",
      });

      // Add page number at the bottom center
      doc.text(
        `Page ${currentPage}`,
        pageSize.width / 2,
        pageSize.height - 10,
        { align: "center" }
      );

      onShowToast({
        type: ToastType.success,
        title: <CheckOutlined />,
        position: ToastOpen.centerTop,
        content: "Report generated successfully",
      });
    };

    // Define table columns
    const columns = [
      { title: "Sr. No.", dataKey: "index" },
      { title: "Project Category", dataKey: "projectCategoryName" },
      { title: "Project Name", dataKey: "projectName" },
      { title: "Task Title", dataKey: "taskTitle" },
      { title: "Work Title", dataKey: "workTitle" },
      { title: "Assigned By", dataKey: "managerName" },
      { title: "Assigned To", dataKey: "employeeName" },
      { title: "Work Category", dataKey: "workCategoryName" },
      { title: "Start Date", dataKey: "startDate" },
      { title: "End Date", dataKey: "endDate" },
      { title: "Estimated Time (min)", dataKey: "taskEstimatedTime" },
      { title: "Actual Time (min)", dataKey: "taskActualTime" },
      { title: "Priority", dataKey: "priority" },
    ];

    // Generate formatted rows
    const rows = data.map((row, index) => {
      return {
        index: index + 1,
        projectName: row.projectName || "-",
        workTitle: row.workTitle || "-",
        projectCategoryName: row.projectCategoryName || "-",
        workCategoryName: row.workCategoryName || "-",
        taskTitle: row.taskTitle || "-",
        startDate: row.startDate
          ? DateUtils.format(row.startDate, "dd-MMM-yyyy")
          : "-",
        endDate: row.endDate
          ? DateUtils.format(row.endDate, "dd-MMM-yyyy")
          : "-",
        taskEstimatedTime: row.taskEstimatedTime
          ? `${row.taskEstimatedTime} min`
          : "-",
        taskActualTime: row.taskActualTime ? `${row.taskActualTime} min` : "-",
        priority: ePriority[row.priority] || "-",
        managerName: row.managerName || "-",
        employeeName: row.employeeName || "-",
      };
    });

    let finalY = 30; // Starting Y position for the table

    // Generate header for the first page
    generateHeader(1, doc.internal.pageSize);

    // Generate the table
    autoTable(doc, {
      head: [columns.map((col) => col.title)],
      body: rows.map((row) =>
        columns.map((col) => row[col.dataKey as keyof typeof row])
      ),
      startY: finalY,
      theme: "grid",
      margin: { top: 30 },
      styles: {
        overflow: "linebreak",
        fontSize: 5,
      },
      headStyles: {
        fillColor: [35, 16, 74],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      bodyStyles: {
        valign: "top",
      },
      rowPageBreak: "avoid",
      didDrawPage: function (data: { pageNumber: number }) {
        // ✅ Fix TypeScript Error
        generateHeader(data.pageNumber, doc.internal.pageSize);
      },
    });

    doc.save("Project_Report.pdf");
  };

  const exportExcel = (data: WorkData[]): void => {
    if (!data || data.length === 0) return;

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    const fileName = "Project_Report";

    // Format data for Excel export
    const formattedData = data.map((row, index) => ({
      "Sr. No.": index + 1,
      "Project Name": row.projectName,
      "Work Title": row.workTitle,
      "Project Category": row.projectCategoryName || "-",
      "Work Category": row.workCategoryName || "-",
      "Task Title": row.taskTitle || "-",
      "Start Date": new Date(row.startDate).toLocaleDateString("en-GB"),
      "End Date": new Date(row.endDate).toLocaleDateString("en-GB"),
      "Estimated Time (min)": row.taskEstimatedTime || "-",
      "Actual Time (min)": row.taskActualTime || "-",
      Priority: ePriority[row.priority] || "-",
      "Assigned By": row.managerName || "-",
      "Assigned To": row.employeeName || "-",
    }));

    // Convert JSON data to worksheet
    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Work Data");

    // Generate Excel file and trigger download
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: fileType });
    const url = window.URL.createObjectURL(dataBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName + fileExtension;
    a.click();
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name='register'
        onFinish={onFinish}
        // initialValues={defaultValues}
        layout='vertical'
        onValuesChange={(e) => {
          setFormValues(form.getFieldsValue());
        }}
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          {/* <Row gutter={16}>
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  Generate Reports
                </h2>
              </Col>
            </Space>
          </Row> */}
          <Row
            gutter={16}
            className='formRowHeight'
          >
            <Col span={24}>
              <Form.Item
                name='generateReport'
                label='Generate Report'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Select Generate Report"),
                  },
                ]}
              >
                <Select
                  placeholder='Select Generate Report'
                  style={{ width: "100%" }}
                >
                  {generateReportOptions.map((option) => (
                    <Select.Option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Project Report : reportType */}
          {formValues.generateReport ===
            eGENERATE_REPORT_TYPE.PROJECT_REPORT && (
            <Row
              gutter={16}
              className='formRowHeight'
            >
              <Col span={24}>
                <Form.Item
                  name='reportType'
                  label='Report Type'
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("Select Report Type"),
                    },
                  ]}
                >
                  <Select
                    placeholder='Select Report Type'
                    style={{ width: "100%" }}
                  >
                    {reportTypeOptions.map((option) => (
                      <Select.Option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Project Report : reportType */}
          {formValues.generateReport ===
            eGENERATE_REPORT_TYPE.PROJECT_REPORT && (
            <Row
              gutter={16}
              className='formRowHeight'
            >
              <Col span={24}>
                {/* <Form.Item
                  name="projectName"
                  label="Project Name"
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("Select Project Name"),
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Project Name"
                    style={{ width: "100%" }}
                  >
                    {projectNameOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item> */}
                <Form.Item
                  label='Project Name'
                  name={"id"}
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("Select Project Name"),
                    },
                  ]}
                >
                  <CustomDropdown
                    endPoint={"/GetProjectList"}
                    value={formValues.id}
                    // addPaylod={{ roleUniqueId: eRoleUniqueId.EMPLOYEE }}
                    optionName='name'
                    selectedName={formValues.id}
                    onChange={(selected: any) => {
                      form.setFieldsValue({
                        id: selected.id,
                      });

                      setFormValues({
                        ...formValues,
                        id: selected.id,
                      });
                    }}
                    placeHolder='Select Project Name'
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Employee Report : employeeName */}
          {formValues.generateReport ===
            eGENERATE_REPORT_TYPE.EMPLOYEE_REPORT && (
            <Row
              gutter={16}
              className='formRowHeight'
            >
              <Col span={24}>
                <Form.Item
                  label='Employee Name'
                  name={"employeeId"}
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("Select Employee Name"),
                    },
                  ]}
                >
                  <CustomDropdown
                    endPoint={"/GetUserList"}
                    value={formValues.employeeName}
                    // addPaylod={{ roleUniqueId: "EMPLOYEE" }}
                    optionName='fullName'
                    selectedName={formValues.employeeName}
                    onChange={(selected: any) => {
                      form.setFieldsValue({
                        employeeId: selected.id,
                      });

                      setFormValues({
                        ...formValues,
                        employeeId: selected.id,
                      });
                    }}
                    placeHolder='Select Employee Name'
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {/* {Project Report : reportFormat */}
          <Row
            gutter={16}
            className='formRowHeight'
          >
            <Col span={24}>
              <Form.Item
                name='reportFormat'
                label='Report Format'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Select Report Format"),
                  },
                ]}
              >
                <Select
                  placeholder='Select Report Format'
                  style={{ width: "100%" }}
                >
                  {reportFormatOptions.map((option) => (
                    <Select.Option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className='formFooter'>
          <Button
            type='default'
            disabled={isLoading}
            className='btnDimensions'
            onClick={props.onCloseDrawer}
          >
            Cancel
          </Button>
          <Button
            type='primary'
            className='btnDimensions'
            onClick={form.submit}
            disabled={isLoading}
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  color: "#fff",
                }}
              >
                <Loader />
                <span>Generating...</span>{" "}
              </div>
            ) : (
              "Generate"
            )}
          </Button>
        </FormFooter>
      </Form>
    </FormLayout>
  );
};

export default GenerateReports;
